/* eslint-disable no-underscore-dangle */

const eventing = require('../../helpers/eventing');
const arrayRemoveItem = require('../../helpers/arrayRemoveItem');
const PeerConnectionDefault = require('./peer_connection')();
const SubscriberPeerConnectionQueue = require('./subscriberPeerConnectionQueue');

class SinglePeerConnectionController {
  constructor(session, deps = {}) {
    this._session = session;
    this.PeerConnection = deps.PeerConnection || PeerConnectionDefault;
    this._subscriberCreateQueue = new SubscriberPeerConnectionQueue();
    this._originalSendMessages = {};
    this._tracks = [];
    this._subscriberPcs = {};
    this._sendMessage = () => {};
    eventing(this);
  }

  parseOptions(options) {
    return {
      ...options,
      sendMessage: (type, payload) => this.trigger('sendMessage', type, payload),
    };
  }

  addTrackAddedListener() {
    if (!this._peerConnection) {
      return;
    }

    // override the trackAdded listener so we can route the tracks to the proper subscriber.
    this._peerConnection.on('trackAdded', (stream) => {
      const { track } = stream;
      if (this._tracks.includes(track.id)) {
        return;
      }
      // We store all tracks per Subscriber.
      this._subscriberPcs[this._subscriberPcToAdd._id].push(track.id);
      // Also store all tracks per SinglePeerConnection
      this._tracks.push(track.id);
      this._subscriberPcToAdd._onTrackAdded(stream);
    });
  }

  // Singleton to make sure we are using only one PC when SPC. If SPC, we will add Subscriber
  // specific options to handle analytics and negotiation per Subscriber. This will take care
  // to send the answer back to Rumor by its respective Subcriber and not multiple answers.
  // It will instantiate a new regular PC for all other cases.
  getPeerConnection(opt, subscriberPc) {
    this._subscriberPcToAdd = subscriberPc;
    this._sendMessage = opt.sendMessage;
    this._originalSendMessages[subscriberPc._id] = this._sendMessage;

    if (this._peerConnection) {
      this._peerConnection.addOptions(opt);
    } else {
      this._firstSubscriberPc = subscriberPc._id;
      // Add listener for PeerConnection sendMessage, so we can properly send the message from
      // the PC to Rumor.
      this.on('sendMessage', (type, payload) => {
        this._sendMessage(type, payload);
      });

      const parsedOptions = this.parseOptions(opt);
      this._peerConnection = new this.PeerConnection(parsedOptions);

      // Add SPC trackAdded listener, which will take care all the tracks for all the subs.
      this.addTrackAddedListener();
    }

    this._subscriberPcs[subscriberPc._id] = [];

    return this._peerConnection;
  }

  removeMessageSender(subscriberPcId) {
    delete this._originalSendMessages[subscriberPcId];
    const originalSenders = Object.values(this._originalSendMessages);
    this._sendMessage = originalSenders[originalSenders.length - 1] || function () {};
  }

  removeSubscriber(subscriberPcId, transceivers) {
    this.removeMessageSender(subscriberPcId);
    this._peerConnection.stopTransceivers(transceivers);
  }

  removeFromSinglePeerConnection(subscriberPcId) {
    if (this._firstSubscriberPc === subscriberPcId) {
      this._peerConnection.iceRestart();
      this._peerConnection.generateOfferAndSend();
    } else {
      this._peerConnection.generateOffer();
    }

    delete this._subscriberPcs[subscriberPcId];
    if (!Object.keys(this._subscriberPcs).length) {
      this._peerConnection.disconnect();
      this._peerConnection = null;
    }
  }

  removeTrack(track, subscriberPcId) {
    arrayRemoveItem(this._tracks, track.id);
    arrayRemoveItem(this._subscriberPcs[subscriberPcId] || [], track.id);
  }

  destroy() {
    this._subscriberPcToAdd = null;
    this._subscriberPcs = null;
    this._session = null;
    this._tracks = null;
    if (this._peerConnection) {
      this._peerConnection.disconnect();
      this._peerConnection = null;
    }
  }

  addSubscriber(subscriberCreate) {
    this._subscriberCreateQueue.add(subscriberCreate);
  }

  subscriberComplete() {
    this._subscriberCreateQueue.next();
  }
}

module.exports = SinglePeerConnectionController;
